import type { FetchETSTicketAttachmentsResponseData } from 'gcs-common/clients/api/entities/tickets/tickets-api-helpers';
import { useGetETSTicketNotificationSelector } from 'gcs-common/clients/api/entities/tickets/tickets-api-selectors';
import IconComponent from 'gcs-common/components/Icon/Icon';
import { ICON, ICON_COLOR } from 'gcs-common/constants/IconConstants';
import { createFileFromUrl } from 'gcs-common/helper/fileHelper';
import { mimeTypeIsImage } from 'gcs-common/helper/mediaHelper';
import documentImage from 'gcs-common/img/document.svg';
import { getMediaFullscreenUrl } from 'gcs-common/slices/uiState/uiStateSelectors';
import { closeMediaFullscreen, openMediaFullscreen } from 'gcs-common/slices/uiState/uiStateSlice';
import type { Response } from 'gcs-common/types/typeHelper';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LocalFilePreview from '../../../LocalFilePreview/LocalFilePreview';
import MediaFullscreen from '../../../MediaFullscreen/MediaFullscreen';
import styles from './styles.module.scss';
import TicketAttachmentNotification from './TicketAttachmentNotification/TicketAttachmentNotification';

interface TicketAttachmentListProps {
  ticketId: string;
  attachments: FetchETSTicketAttachmentsResponseData['attachments'];
}

interface FileAttachements {
  [key: string]: File | null;
}

const TicketAttachmentsList = (props: TicketAttachmentListProps) => {
  const { ticketId, attachments } = props;
  const [fileAttachments, setFileAttachments] = useState<FileAttachements>({} as FileAttachements);
  const dispatch = useDispatch();
  const ticketNotification = useGetETSTicketNotificationSelector(ticketId);
  const fullScreenUrl: string = useSelector(getMediaFullscreenUrl);

  const handleOpenFullScreenMedia =
    (attachment: Response<'/chat/ets/tickets/{ticket_id}/attachments', 'get'>['attachments'][number]) => () => {
      dispatch(openMediaFullscreen({ mediaFullscreenUrl: attachment.url }));
    };

  // runs only when component unmount.
  useEffect(() => {
    return () => {
      dispatch(closeMediaFullscreen());
    };
  }, [dispatch]);

  useEffect(() => {
    const fetchFiles = async () => {
      const pdfAttachments = attachments.filter((attachment) => attachment.mimeType === 'application/pdf');

      const fetchPromises = pdfAttachments.map((attachment) =>
        createFileFromUrl(attachment.url, attachment.name, attachment.mimeType).then((file) => ({
          id: attachment.id,
          file,
        })),
      );

      const fetchedPdfAttachments = await Promise.all(fetchPromises);

      // Create the key-value pair map with attachment id as key
      const fileAttachmentsMap = attachments.reduce<FileAttachements>((acc, attachment) => {
        const fetchedPdf = fetchedPdfAttachments.find((pdfAttachment) => pdfAttachment.id === attachment.id);
        // Assign the file if it exists, otherwise set the value to null or undefined
        acc[attachment.id] = fetchedPdf ? fetchedPdf.file : null;
        return acc;
      }, {});

      setFileAttachments(fileAttachmentsMap);
    };

    fetchFiles()
      .then(() => {})
      .catch(() => {});
  }, [attachments.length]);

  return (
    <div className={styles.imageContainer}>
      {attachments.map((attachment) => (
        <div className={styles.imageWrapper} key={attachment.id}>
          {ticketNotification &&
            moment(attachment.createdAt).isAfter(ticketNotification.lastViewedAt) &&
            !attachment.fromBeem && <TicketAttachmentNotification />}

          {attachment.mimeType && mimeTypeIsImage(attachment.mimeType) ? (
            <button className={styles.imgButton} type="button" onClick={handleOpenFullScreenMedia(attachment)}>
              <img src={attachment.thumbnailUrl} alt={attachment.name} />
            </button>
          ) : (
            <LocalFilePreview file={fileAttachments[attachment.id]} filePlaceHolder={documentImage} />
          )}

          <div className={styles.buttonActions}>
            <div className={styles.downloadLink}>
              <a href={attachment.url} download={attachment.name} target="_blank" rel="noreferrer">
                <IconComponent Icon={ICON.DOWNLOAD} color={ICON_COLOR.DARKEST_GREY} />
              </a>
            </div>
          </div>
        </div>
      ))}
      {fullScreenUrl && <MediaFullscreen mediaFullscreenUrl={fullScreenUrl} />}
    </div>
  );
};

export default TicketAttachmentsList;
